import {ChangeDetectionStrategy, Component, InputSignal, OutputEmitterRef, Signal, WritableSignal, computed, inject, input, output, signal} from '@angular/core';
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {DialogService} from "../../services/dialog.service";
import {ProfileService} from "../../services/profile.service";
import {FormButtonComponent} from "../form-button/form-button.component";
import {HeadlineComponent} from "../headline/headline.component";
import {TooltipComponent} from "../tooltip/tooltip.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {ContentComponent} from "../content/content.component";

export interface EmojiPillItem {
  referenceId: number;
  emoji: string;
  userId: number;
  userName: string;
}

@Component({
  selector: 'app-emoji-pill',
  imports: [
    DialogModule,
    FormButtonComponent,
    HeadlineComponent,
    TooltipComponent,
    TranslatePipe,
    ContentComponent
  ],
  templateUrl: './emoji-pill.component.html',
  styleUrl: './emoji-pill.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmojiPillComponent {
  public dialogService: DialogService = inject(DialogService);
  private profileService: ProfileService = inject(ProfileService);

  public emojis: InputSignal<EmojiPillItem[]> = input<EmojiPillItem[]>([]);
  public totalCount: InputSignal<number | null> = input<number | null>(null);
  public ownMessage: InputSignal<boolean> = input<boolean>(false);
  public height: InputSignal<string> = input<string>('auto');
  public textSize: InputSignal<string> = input<string>('0.75rem');
  public deleteEmoji: OutputEmitterRef<number> = output<number>();
  public openRequest: OutputEmitterRef<void> = output<void>();

  public dialogVisible: WritableSignal<boolean> = signal(false);

  public totalEmojiCount: Signal<number> = computed(() => this.totalCount() ?? this.emojis().length);

  public previewEmojis: Signal<string[]> = computed(() => {
    const uniqueEmojis: string[] = [];
    this.emojis().forEach((emojiItem) => {
      if (!uniqueEmojis.includes(emojiItem.emoji)) {
        uniqueEmojis.push(emojiItem.emoji);
      }
    });
    return uniqueEmojis.slice(0, 2);
  });

  public sortedEmojis: Signal<EmojiPillItem[]> = computed(() => {
    const currentUserId = this.profileService.profile()?.user?.id;
    const reactions = [...this.emojis()];
    if (!currentUserId) {
      return reactions;
    }

    return reactions.sort((a, b) => {
      const aOwn = a.userId === currentUserId;
      const bOwn = b.userId === currentUserId;
      if (aOwn === bOwn) {
        return 0;
      }
      return aOwn ? -1 : 1;
    });
  });

  public openDialog(): void {
    if (this.totalEmojiCount() === 0) {
      return;
    }
    this.openRequest.emit();
    this.dialogVisible.set(true);
  }

  public closeDialog(): void {
    this.dialogVisible.set(false);
  }

  public isOwnReaction(reaction: EmojiPillItem): boolean {
    return reaction.userId === this.profileService.profile()?.user?.id;
  }

  public removeReaction(event: MouseEvent, referenceId: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.deleteEmoji.emit(referenceId);
  }
}
